import type { OnResolveArgs, OnResolveResult, Plugin, PluginBuild } from "esbuild-wasm";
import type { BundleConfigOptions } from "../configs/options";
import type { EVENTS } from "../configs/events";
import type { STATE } from "../configs/state";

import { resolveExports, legacy } from "../utils/resolve-exports";
import { parsePackageName as parsePackageName } from "../utils/parse-package-name";

import { HTTP_NAMESPACE } from "./http";

import { isBareImport } from "../utils/path";
import { getRequest } from "../utils/fetch-and-cache";

import { getCDNUrl, getCDNStyle } from "../utils/util-cdn";
import { resolveImports } from "../utils/resolve-imports";
import { DEFAULT_CDN_HOST } from "../utils/util-cdn";

/** CDN Plugin Namespace */
export const CDN_NAMESPACE = "cdn-url";

/**
 * Resolution algorithm for the esbuild CDN plugin
 *
 * @param cdn The default CDN to use
 * @param logger Console log
 */
export const CDN_RESOLVE = (cdn = DEFAULT_CDN_HOST, events: typeof EVENTS, build: PluginBuild) => {
  return async (args: OnResolveArgs): Promise<OnResolveResult | undefined> => {
    if (isBareImport(args.path)) {
      // Support a different default CDN + allow for custom CDN url schemes
      const { path: argPath, origin } = getCDNUrl(args.path, cdn);

      // npm standard CDNs, e.g. unpkg, skypack, esm.sh, etc...
      const NPM_CDN = getCDNStyle(origin) == "npm";

      // Heavily based off of https://github.com/egoist/play-esbuild/blob/main/src/lib/esbuild.ts
      const parsed = parsePackageName(argPath);
      let subpath = parsed.path;
      let pkg = args.pluginData?.pkg ?? {};

      // Resolving imports from the package.json, if said import starts with "#"
      // If an import starts with "#" then it's a subpath-import
      // https://nodejs.org/api/packages.html#subpath-imports
      if (argPath[0] == "#") {
        const path = resolveImports({ ...pkg, exports: pkg.imports }, argPath, {
          browser: build.initialOptions.platform == "node" ? false : true,
          require: args.kind === "require-call" || args.kind === "require-resolve",
        });

        if (typeof path === "string") {
          subpath = path.replace(/^\.?\/?/, "/");

          if (subpath && subpath[0] !== "/") subpath = `/${subpath}`;

          const version = NPM_CDN ? "@" + pkg.version : "";
          const {
            url: { href },
          } = getCDNUrl(`${pkg.name}${version}${subpath}`);
          return {
            namespace: HTTP_NAMESPACE,
            path: href,
            pluginData: { pkg },
          };
        }
      }

      // Are there an dependecies???? Well Goood.
      const depsExists = "dependencies" in pkg || "devDependencies" in pkg || "peerDependencies" in pkg;
      if (depsExists && !/\S+@\S+/.test(argPath)) {
        const { devDependencies = {}, dependencies = {}, peerDependencies = {} } = pkg;

        const deps = Object.assign({}, devDependencies, peerDependencies, dependencies);
        const keys = Object.keys(deps);

        if (keys.includes(argPath)) parsed.version = deps[argPath];
      }

      // If the CDN supports package.json and some other npm stuff, it counts as an npm CDN
      if (NPM_CDN) {
        try {
          const { url: PACKAGE_JSON_URL } = getCDNUrl(`${parsed.name}@${parsed.version}/package.json`, origin);

          // Strongly cache package.json files
          pkg = await getRequest(PACKAGE_JSON_URL, true).then((res) => res.json());
          const path =
            resolveExports(pkg, subpath ? "." + subpath.replace(/^\.?\/?/, "/") : ".", {
              browser: build.initialOptions.platform == "node" ? false : true,
              require: args.kind === "require-call" || args.kind === "require-resolve",
            }) || legacy(pkg, { browser: build.initialOptions.platform == "node" ? false : true });

          if (typeof path === "string") subpath = path.replace(/^\.?\/?/, "/").replace(/\.js\.js$/, ".js");

          if (subpath && subpath[0] !== "/") subpath = `/${subpath}`;
        } catch (e) {
          events
            .emit(
              "logger.warn",
              `You may want to change CDNs. The current CDN ${
                !/unpkg\.com/.test(origin) ? `"${origin}" doesn't` : `path "${origin}${argPath}" may not`
              } support package.json files.\nThere is a chance the CDN you're using doesn't support looking through the package.json of packages. bundlejs will switch to inaccurate guesses for package versions. For package.json support you may wish to use https://unpkg.com or other CDN's that support package.json.`
            )
            .emit("logger.warn", e);
        }
      }

      // If the CDN is npm based then it should add the parsed version to the URL
      // e.g. https://unpkg.com/spring-easing@v1.0.0/
      const version = NPM_CDN ? "@" + parsed.version : "";
      const { url } = getCDNUrl(`${parsed.name}${version}${subpath}`, origin);

      return {
        namespace: HTTP_NAMESPACE,
        path: url.toString(),
        pluginData: { pkg },
      };
    }
  };
};

/**
 * Esbuild CDN plugin
 *
 * @param cdn The default CDN to use
 * @param logger Console log
 */
export const CDN = (events: typeof EVENTS, state: typeof STATE, config: BundleConfigOptions): Plugin => {
  // Convert CDN values to URL origins
  const { origin: cdn } = !/:/.test(config?.cdn ?? "") ? getCDNUrl(config?.cdn + ":") : getCDNUrl(config?.cdn ?? "");
  return {
    name: CDN_NAMESPACE,
    setup(build) {
      // Resolve bare imports to the CDN required using different URL schemes
      build.onResolve({ filter: /.*/ }, CDN_RESOLVE(cdn, events, build));
      build.onResolve({ filter: /.*/, namespace: CDN_NAMESPACE }, CDN_RESOLVE(cdn, events, build));
    },
  };
};
